import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

import { onSessionReset } from '@shared/session';

interface ScreenNameState {
  /** 화면이 알려 준 대상 이름(펀드 이름 · 회사 이름). 주소 key(react-router `location.key`) → 이름 */
  names: Record<string, string>;
}

/**
 * 화면의 대상 이름. BackBar 글자 "상위 화면 - 현재 화면" 의 현재 화면 쪽(상위는 화면 트리 — app/layouts/screen-tree).
 * 템플릿이 바뀌어 머리가 다시 그려져도 남도록 모듈 store 에 둔다.
 */
export const screenNameStore = createStore<ScreenNameState>()(() => ({ names: {} }));

// 로그아웃 · 역할 바꿈이면 새로 시작한다
onSessionReset(() => screenNameStore.setState({ names: {} }));

/**
 * 화면이 BackBar 에 쓸 대상 이름을 알린다(펀드 상세 = 펀드 이름, 포트폴리오 상세 = 회사 이름).
 * 이름이 없으면(자료를 받는 중) 화면 이름을 쓴다.
 */
export function useScreenName(name: string | undefined): void {
  const { key } = useLocation();
  useLayoutEffect(() => {
    if (!name) return;
    screenNameStore.setState((state) =>
      state.names[key] === name ? state : { names: { ...state.names, [key]: name } },
    );
  }, [key, name]);
}

/** 지금 화면의 이름 — 화면이 알린 대상 이름, 없으면 `title`(화면 이름) */
export function useCurrentScreenName(title: string): string {
  const { key } = useLocation();
  return useStore(screenNameStore, (state) => state.names[key]) ?? title;
}
